// src/components/dashboard/Calendar.jsx
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  format,
  startOfMonth,
  endOfMonth,
  addMonths,
  subMonths,
  parseISO,
  startOfWeek,
  endOfWeek
} from 'date-fns';
import { enUS, nl } from 'date-fns/locale';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  VideoCameraIcon,
  CalendarIcon,
  ClockIcon,
  UserIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import { toast } from 'sonner';
import { getCalendarSessions, sessionAPI } from '../../services/api';
import { useWebSocket } from '../../hooks/useWebSocket';

// Helper to format date to YYYY-MM-DD for API calls
const toYYYYMMDD = (date) => {
  return format(date, 'yyyy-MM-dd');
};

// Helper functions with i18n support
function formatDate(dateString, locale) {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString(locale === 'nl' ? 'nl-NL' : 'en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric'
  });
}

function formatTime(dateStr) {
  if (!dateStr) return 'N/A';
  const date = new Date(dateStr);
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
}

// Format duration from seconds to human-readable format
function formatDuration(seconds, t) {
  if (!seconds || seconds < 0) return 'N/A';
  
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  
  if (hours > 0) {
    return t('calendar.duration.hoursMinutesSeconds', { hours, minutes, seconds: secs });
  } else if (minutes > 0) {
    return t('calendar.duration.minutesSeconds', { minutes, seconds: secs });
  } else {
    return t('calendar.duration.seconds', { seconds: secs });
  }
}

// Optimized timer components that only update when visible
function LiveSessionTimer({ secondsSinceStart, actualStartTime, t }) {
  const calculateTimeAgo = useCallback(() => {
    let elapsed;
    
    if (secondsSinceStart !== null && secondsSinceStart !== undefined) {
      const initialSeconds = secondsSinceStart;
      const startTime = Date.now() - (initialSeconds * 1000);
      elapsed = Math.floor((Date.now() - startTime) / 1000);
    } else if (actualStartTime) {
      elapsed = Math.floor((Date.now() - new Date(actualStartTime).getTime()) / 1000);
    } else {
      return '';
    }
    
    const minutes = Math.floor(elapsed / 60);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) {
      return t('calendar.time.startedHoursAgo', { hours, minutes: minutes % 60 });
    } else if (minutes > 0) {
      return t('calendar.time.startedMinutesAgo', { minutes });
    } else {
      return t('calendar.time.justStarted');
    }
  }, [secondsSinceStart, actualStartTime, t]);

  return <span className="text-xs text-green-600">{calculateTimeAgo()}</span>;
}

function CountdownTimer({ startTime, t }) {
  const calculateTimeUntil = useCallback(() => {
    const now = new Date();
    const sessionTime = new Date(startTime);
    const diff = sessionTime.getTime() - now.getTime();

    if (diff <= 0) {
      return t('calendar.time.startingSoon');
    }

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (days > 1) {
      return t('calendar.time.daysCount', { count: days });
    } else if (days === 1) {
      return t('calendar.time.oneDayHours', { hours });
    } else if (hours > 0) {
      return t('calendar.time.hoursMinutes', { hours, minutes });
    } else {
      return t('calendar.time.minutesCount', { count: minutes });
    }
  }, [startTime, t]);

  return <span>{calculateTimeUntil()}</span>;
}

function DynamicTimeAgo({ time, t }) {
  const calculateTimeAgo = useCallback(() => {
    if (!time) return '';
    
    const diff = new Date().getTime() - new Date(time).getTime();
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 1) {
      return t('calendar.time.daysAgo', { count: days });
    } else if (days === 1) {
      return t('calendar.time.oneDayAgo');
    } else if (hours > 0) {
      return t('calendar.time.hoursMinutesAgo', { hours, minutes: minutes % 60 });
    } else if (minutes > 0) {
      return t('calendar.time.minutesAgo', { count: minutes });
    } else {
      return t('calendar.time.justNow');
    }
  }, [time, t]);

  return <span>{calculateTimeAgo()}</span>;
}

// Helper function to normalize tutor/teacher information
function normalizeTutorInfo(session) {
  return {
    ...session,
    tutor_name: session.tutor_name || 
                session.tutorName || 
                session.tutor?.name ||
                session.teacher_name ||
                session.teacherName ||
                session.teacher?.name ||
                session.tutor?.displayName ||
                session.teacher?.displayName ||
                null
  };
}

// Recording Consent Modal Component
function RecordingConsentModal({ session, onConsent, onCancel }) {
  const { t } = useTranslation();
  const [isChecked, setIsChecked] = useState(false);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
        <div className="flex items-center mb-4">
          <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
            <VideoCameraIcon className="w-6 h-6 text-red-600" />
          </div>
          <h3 className="ml-3 text-lg font-semibold text-slate-900">{t('calendar.recording.modalTitle')}</h3>
        </div>
        
        <div className="mb-6">
          <p className="text-sm text-slate-600 mb-3">
            {t('calendar.recording.modalDescription')}
          </p>
          <p className="text-xs text-slate-500">
            {t('calendar.recording.modalDisclaimer')}
          </p>
        </div>

        <div className="mb-6">
          <label className="flex items-start">
            <input
              type="checkbox"
              checked={isChecked}
              onChange={(e) => setIsChecked(e.target.checked)}
              className="mt-1 rounded border-slate-300 text-cyan-600 focus:ring-cyan-500"
            />
            <span className="ml-2 text-sm text-slate-700">
              {t('calendar.recording.consentCheckbox')}
            </span>
          </label>
        </div>

        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors text-sm font-medium"
          >
            {t('calendar.recording.cancel')}
          </button>
          <button
            onClick={onConsent}
            disabled={!isChecked}
            className={`flex-1 px-4 py-2 rounded-lg transition-colors text-sm font-medium ${
              isChecked
                ? 'bg-cyan-600 text-white hover:bg-cyan-700'
                : 'bg-slate-200 text-slate-400 cursor-not-allowed'
            }`}
          >
            {t('calendar.recording.acceptAndJoin')}
          </button>
        </div>
      </div>
    </div>
  );
}

// MAIN COMPONENT
function Calendar({ userRole, currentUserRole, institutionName }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentMonth, setCurrentMonth] = useState(startOfMonth(new Date()));
  const [monthSessions, setMonthSessions] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasInitialLoad, setHasInitialLoad] = useState(false);
  const [recentlyUpdatedSessions, setRecentlyUpdatedSessions] = useState(new Set());
  const [sessionStatuses, setSessionStatuses] = useState({});
  const [recordingConsentSession, setRecordingConsentSession] = useState(null);
  const [, forceUpdate] = useState(0);
  const updateTimerRef = useRef(null);

  // Use the main WebSocket hook
  const { sendMessage: wsSendMessage, addMessageHandler, isConnected } = useWebSocket();

  // Get date-fns locale based on current language
  const dateLocale = i18n.language === 'nl' ? nl : enUS;

  // Determine actual user role
  const actualUserRole = currentUserRole || userRole;
  const isTeacher = actualUserRole === 'teacher';

  // Smart update scheduler - updates UI at critical time points
  const scheduleNextUpdate = useCallback(() => {
    if (updateTimerRef.current) {
      clearTimeout(updateTimerRef.current);
    }

    const now = new Date();
    const allSessions = Object.values(monthSessions).flat();
    let nextUpdateTime = null;

    allSessions.forEach(session => {
      const sessionId = parseInt(session.id, 10);
      const status = sessionStatuses[sessionId] || session.session_status;
      const startTime = new Date(session.startTime || session.start_time);
      const endTime = new Date(session.endTime || session.end_time);
      
      if (status === 'ended' || session.ended) return;
      
      if (now < startTime) {
        const fiveMinsBefore = new Date(startTime.getTime() - 5 * 60 * 1000);
        if (fiveMinsBefore > now && (!nextUpdateTime || fiveMinsBefore < nextUpdateTime)) {
          nextUpdateTime = fiveMinsBefore;
        }
      }
      
      if (startTime > now && (!nextUpdateTime || startTime < nextUpdateTime)) {
        nextUpdateTime = startTime;
      }
      
      if (endTime > now && (!nextUpdateTime || endTime < nextUpdateTime)) {
        nextUpdateTime = endTime;
      }
    });

    if (nextUpdateTime) {
      const delay = Math.max(0, nextUpdateTime.getTime() - now.getTime());
      const delayMinutes = Math.floor(delay / 60000);
      
      console.log(`Next calendar update scheduled in ${delayMinutes} minutes`);
      
      updateTimerRef.current = setTimeout(() => {
        forceUpdate(prev => prev + 1);
        scheduleNextUpdate();
      }, delay);
    }
  }, [monthSessions, sessionStatuses]);

  useEffect(() => {
    scheduleNextUpdate();
    
    return () => {
      if (updateTimerRef.current) {
        clearTimeout(updateTimerRef.current);
      }
    };
  }, [scheduleNextUpdate]);

  // Calculate calendar range
  const calendarRange = useMemo(() => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
    
    return { calendarStart, calendarEnd };
  }, [currentMonth]);

  // Set document title
  useEffect(() => {
    document.title = `${institutionName || t('calendar.defaultInstitution')} - ${t('navigation.calendar')}`;
  }, [institutionName, t]);

  // Navigate to session
  const handleJoinSession = (sessionId) => {
    navigate(`/sessions/${sessionId}`);
  };

  // Fetch session details function
  const fetchSessionDetails = async (sessionId) => {
    try {
      const response = await sessionAPI.getSession(sessionId);
      if (response.success) {
        // The session should already include course data from the backend
        return response.data;
      }
      return null;
    } catch (error) {
      console.error('Failed to fetch session:', error);
      return null;
    }
  };

  // Function to fetch calendar sessions AND live status
  const fetchAndProcessSessions = useCallback(async () => {
    setIsLoading(true);
    setError('');

    const { calendarStart, calendarEnd } = calendarRange;
    const startDate = toYYYYMMDD(calendarStart);
    const endDate = toYYYYMMDD(calendarEnd);

    try {
      const response = await getCalendarSessions(startDate, endDate);
      
      let upcomingSessions = [];
      try {
        const upcomingResponse = await sessionAPI.getUpcomingSessions();
        if (upcomingResponse.success && Array.isArray(upcomingResponse.data)) {
          upcomingSessions = upcomingResponse.data;
        }
      } catch (err) {
        console.log('Could not fetch upcoming sessions for live status');
      }

      if (response.success && Array.isArray(response.data)) {
        const sessionsByDate = {};
        const initialStatuses = {};
        
        const liveSessionIds = new Set();
        upcomingSessions.forEach(session => {
          if (session.started === true || session.is_started === true || session.session_status === 'live' || session.status === 'live') {
            liveSessionIds.add(session.id);
            initialStatuses[session.id] = 'live';
          }
        });
        
        response.data.forEach(session => {
          const sessionDate = session.startTime || session.start_time;
          if (!sessionDate) {
            console.warn('Session without start time:', session);
            return;
          }

          try {
            const sessionStartDate = parseISO(sessionDate);
            const dateKey = toYYYYMMDD(sessionStartDate);
            if (!sessionsByDate[dateKey]) {
              sessionsByDate[dateKey] = [];
            }
            
            if (liveSessionIds.has(session.id)) {
              session.started = true;
              initialStatuses[session.id] = 'live';
            }
            
            const normalizedSession = normalizeTutorInfo(session);
            sessionsByDate[dateKey].push(normalizedSession);
            
            if (session.started === true) {
              initialStatuses[session.id] = 'live';
            } else if (session.session_status || session.status || session.sessionStatus) {
              initialStatuses[session.id] = session.session_status || session.status || session.sessionStatus;
            }
          } catch (parseError) {
            console.error('Error parsing session date:', sessionDate, parseError);
          }
        });
        
        setMonthSessions(sessionsByDate);
        setSessionStatuses(prev => ({ ...prev, ...initialStatuses }));
        setHasInitialLoad(true);
      } else {
        setError(response.message || t('calendar.errors.loadFailed'));
        setMonthSessions({});
      }
    } catch (err) {
      if (err.response?.status === 429 || err.message?.includes('429')) {
        setError(t('calendar.errors.tooManyRequests'));
      } else {
        setError(err.message || t('calendar.errors.fetchError'));
      }
      setMonthSessions({});
    } finally {
      setIsLoading(false);
    }
  }, [calendarRange, t]);

  // Initial load on mount
  useEffect(() => {
    fetchAndProcessSessions();
  }, []);

  // Fetch when month changes
  useEffect(() => {
    if (hasInitialLoad) {
      fetchAndProcessSessions();
    }
  }, [currentMonth, fetchAndProcessSessions, hasInitialLoad]);

// Subscribe to WebSocket updates
  useEffect(() => {
    if (!isConnected) return;

    wsSendMessage({
      type: 'subscribe_calendar',
      startDate: toYYYYMMDD(calendarRange.calendarStart),
      endDate: toYYYYMMDD(calendarRange.calendarEnd)
    });

    wsSendMessage({
      type: 'subscribe_institution'
    });

    const unsubscribeSessionUpdate = addMessageHandler('session_update', (data) => {
      console.log('Calendar received session_update:', data);
      
      if (data.action === 'created' && data.session) {
        const sessionDate = data.session.startTime || data.session.start_time;
        if (!sessionDate) {
          console.warn('Session created without start time:', data.session);
          return;
        }

        const normalizedSession = normalizeTutorInfo(data.session);
        
        toast.info(t('calendar.toast.newSession', { title: normalizedSession.title || t('calendar.untitled') }), {
          duration: 4000,
          icon: 'ðŸ“…'
        });
        
        try {
          const parsedDate = parseISO(sessionDate);
          const dateKey = toYYYYMMDD(parsedDate);
          
          setMonthSessions(prev => ({
            ...prev,
            [dateKey]: [...(prev[dateKey] || []), normalizedSession]
          }));
          
          if (normalizedSession.session_status) {
            setSessionStatuses(prev => ({
              ...prev,
              [normalizedSession.id]: normalizedSession.session_status
            }));
          }
        } catch (parseError) {
          console.error('Error parsing session date:', sessionDate, parseError);
        }
      } else if (data.action === 'deleted') {
        const sessionId = data.sessionId || data.session?.id;
        
        if (sessionId) {
          toast.info(t('calendar.toast.sessionRemoved'), { duration: 4000 });
          
          setMonthSessions(prev => {
            const updated = { ...prev };
            Object.keys(updated).forEach(dateKey => {
              updated[dateKey] = updated[dateKey].filter(s => s.id !== sessionId);
              if (updated[dateKey].length === 0) {
                delete updated[dateKey];
              }
            });
            return updated;
          });
          
          setSessionStatuses(prev => {
            const updated = { ...prev };
            delete updated[sessionId];
            return updated;
          });
        }
      }
    });

    const unsubscribeStatusChange = addMessageHandler('session_status_change', async (data) => {
      console.log('Calendar received session_status_change:', data);
      
      const { sessionId, status, sessionData } = data;
      const numericSessionId = parseInt(sessionId, 10);
      
      // Update status in sessionStatuses
      if (status) {
        setSessionStatuses(prev => ({
          ...prev,
          [numericSessionId]: status
        }));
      }
      
      // Find and update the session in monthSessions
      setMonthSessions(prev => {
        const updated = { ...prev };
        let found = false;
        
        // Search across all dates to find the session
        Object.keys(updated).forEach(dateKey => {
          const sessionIndex = updated[dateKey].findIndex(s => 
            parseInt(s.id, 10) === numericSessionId
          );
          
          if (sessionIndex >= 0) {
            found = true;
            const existingSession = updated[dateKey][sessionIndex];
            
            // Start with existing session to preserve all fields
            const updatedSession = {
              ...existingSession,
              session_status: status,
              ended: status === 'ended',
              started: status === 'live'
            };
            
            // If we have sessionData, merge it carefully
            if (sessionData) {
              // Merge all fields from sessionData except actual times (handle those specially)
              Object.keys(sessionData).forEach(key => {
                // Skip actual time fields - we'll handle those specially
                if (key !== 'actual_start_time' && key !== 'actual_end_time') {
                  updatedSession[key] = sessionData[key];
                }
              });
              
              // Handle actual_start_time: prefer sessionData, fallback to existing
              const finalActualStartTime = 
                sessionData.actual_start_time || 
                existingSession.actual_start_time || 
                existingSession.actualStartTime;
              
              if (finalActualStartTime) {
                updatedSession.actual_start_time = finalActualStartTime;
                updatedSession.actualStartTime = finalActualStartTime;
              }
              
              // Handle actual_end_time: prefer sessionData, fallback to existing
              const finalActualEndTime = 
                sessionData.actual_end_time || 
                existingSession.actual_end_time || 
                existingSession.actualEndTime;
              
              if (finalActualEndTime) {
                updatedSession.actual_end_time = finalActualEndTime;
                updatedSession.actualEndTime = finalActualEndTime;
              }
            }
            
            updated[dateKey][sessionIndex] = updatedSession;
            
            console.log('Updated session:', {
              id: updatedSession.id,
              status: updatedSession.session_status,
              actual_start_time: updatedSession.actual_start_time,
              actual_end_time: updatedSession.actual_end_time
            });
          }
        });
        
        if (!found) {
          console.warn(`Session ${numericSessionId} not found in calendar view. It may be outside the current month.`);
        }
        
        return updated;
      });
    });

    const unsubscribeCalendarUpdate = addMessageHandler('calendar_update', (data) => {
      console.log('Calendar received calendar_update:', data);
      
      if (data.action === 'created' && data.session) {
        const normalizedSession = normalizeTutorInfo(data.session);
        const sessionDate = normalizedSession.startTime || normalizedSession.start_time;
        
        if (sessionDate) {
          const dateKey = format(new Date(sessionDate), 'yyyy-MM-dd');
          
          setMonthSessions(prev => {
            const updated = { ...prev };
            
            // Check if session already exists
            if (updated[dateKey]) {
              const exists = updated[dateKey].some(s => s.id === normalizedSession.id);
              if (!exists) {
                updated[dateKey] = [...updated[dateKey], normalizedSession];
              }
            } else {
              updated[dateKey] = [normalizedSession];
            }
            
            return updated;
          });
          
          // Set initial status
          if (normalizedSession.session_status) {
            setSessionStatuses(prev => ({
              ...prev,
              [normalizedSession.id]: normalizedSession.session_status
            }));
          }
          
          // Show notification
          toast.success(t('calendar.toast.newSession', { 
            title: normalizedSession.title || t('calendar.untitled')
          }), {
            duration: 4000
          });
        }
      } else if (data.action === 'deleted' && data.session) {
        // Remove deleted session from all dates
        const sessionId = data.session.id;
        
        setMonthSessions(prev => {
          const updated = { ...prev };
          let removed = false;
          
          // Search through all dates and remove the session
          Object.keys(updated).forEach(dateKey => {
            const filteredSessions = updated[dateKey].filter(s => s.id !== sessionId);
            if (filteredSessions.length !== updated[dateKey].length) {
              removed = true;
              if (filteredSessions.length === 0) {
                delete updated[dateKey];
              } else {
                updated[dateKey] = filteredSessions;
              }
            }
          });
          
          return removed ? updated : prev;
        });
        
        // Remove session status
        setSessionStatuses(prev => {
          const updated = { ...prev };
          delete updated[sessionId];
          return updated;
        });
        
        // Show notification
        toast.info(t('calendar.toast.sessionDeleted'), {
          duration: 3000
        });
      }
    });

    const unsubscribeEnrollmentUpdate = addMessageHandler('enrollment-update', (data) => {
      console.log('Calendar received enrollment-update:', data);
  
      // User was enrolled or unenrolled from a course - refetch calendar
      if (data.action === 'enrolled' || data.action === 'unenrolled') {
        toast.info(t('calendar.toast.courseChanged'), { duration: 3000 });
        fetchAndProcessSessions(); // Refetch all sessions
      }
    });

    const unsubscribeCourseMemberUpdate = addMessageHandler('course-member-update', (data) => {
      console.log('Calendar received course-member-update:', data);
      
      // Course membership changed - refetch calendar
      toast.info(t('calendar.toast.courseChanged'), { duration: 3000 });
      fetchAndProcessSessions(); // Refetch all sessions
    });

    const unsubscribeCourseUpdate = addMessageHandler('course-update', (data) => {
      console.log('Calendar received course-update:', data);
      
      // Course was updated or deleted - refetch calendar
      // This ensures sessions are removed if course is deleted or updated
      if (data.action === 'deleted' || data.action === 'updated') {
        toast.info(t('calendar.toast.courseChanged'), { duration: 3000 });
        fetchAndProcessSessions(); // Refetch all sessions
      }
    });

    // Cleanup
    return () => {
      wsSendMessage({
        type: 'unsubscribe_calendar'
      });
      wsSendMessage({
        type: 'unsubscribe_institution'
      });
      unsubscribeSessionUpdate();
      unsubscribeStatusChange();
      unsubscribeCalendarUpdate();
      unsubscribeEnrollmentUpdate();  
      unsubscribeCourseMemberUpdate();
      unsubscribeCourseUpdate();
    };
  }, [isConnected, calendarRange, wsSendMessage, addMessageHandler, navigate, t]);

  const getSessionsForDate = (date) => {
    const dateKey = toYYYYMMDD(date);
    return monthSessions[dateKey] || [];
  };

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    const firstDay = new Date(date.getFullYear(), date.getMonth(), 1).getDay();
    return firstDay === 0 ? 6 : firstDay - 1;
  };

  const isCurrentDay = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentMonth);
    const firstDay = getFirstDayOfMonth(currentMonth);
    const days = [];

    // Add empty cells for days before the first day of month
    for (let i = 0; i < firstDay; i++) {
      const prevMonthDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 
        getDaysInMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1)) - firstDay + i + 1);
      const sessions = getSessionsForDate(prevMonthDate);
      const hasSessions = sessions.length > 0;

      days.push(
        <div key={`empty-${i}`} className="p-1.5 sm:p-3 text-slate-300 text-xs sm:text-sm relative cursor-pointer hover:bg-slate-50 rounded-lg"
             onClick={() => setSelectedDate(prevMonthDate)}>
          {prevMonthDate.getDate()}
          {hasSessions && (
            <div className="absolute bottom-1 sm:bottom-1.5 left-0 right-0">
              {sessions.length > 8 && (
                <div className="text-center mb-0.5">
                  <span className="text-xs text-cyan-600">+{sessions.length - 8}</span>
                </div>
              )}
              <div className="flex justify-center gap-0.5 sm:gap-1">
                {sessions.slice(0, 8).map((session, idx) => (
                  <div key={idx} className="w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full bg-cyan-500" />
                ))}
              </div>
            </div>
          )}
        </div>
      );
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
      const sessions = getSessionsForDate(date);
      const isSelected = date.toDateString() === selectedDate.toDateString();
      const hasSessions = sessions.length > 0;
      const isCurrent = isCurrentDay(date);
      
      const hasLiveSession = sessions.some(session => {
        const sId = parseInt(session.id, 10);
        const status = sessionStatuses[sId] || session.session_status || session.status;
        return status === 'live' || session.started === true;
      });
      
      const hasRecentlyUpdated = sessions.some(session => {
        const sId = parseInt(session.id, 10);
        return recentlyUpdatedSessions.has(sId);
      });

      days.push(
        <div 
          key={day}
          onClick={() => setSelectedDate(date)}
          className={`relative p-1.5 sm:p-3 cursor-pointer transition-colors rounded-lg sm:rounded-xl text-xs sm:text-sm
            ${isSelected ? 'ring-2 ring-cyan-400' : ''} 
            ${isCurrent 
              ? 'bg-gradient-to-r from-cyan-600 to-cyan-500 text-white shadow-sm' 
              : hasLiveSession
                ? 'bg-green-50 text-green-800 hover:bg-green-100 animate-pulse'
                : hasRecentlyUpdated
                  ? 'bg-yellow-50 text-yellow-800 hover:bg-yellow-100'
                  : hasSessions 
                    ? 'bg-cyan-50 text-cyan-800 hover:bg-cyan-100' 
                    : 'hover:bg-slate-50'
            }`}
        >
          <div className={`font-medium ${isCurrent ? 'text-white' : ''}`}>{day}</div>
          {hasSessions && (
            <div className="absolute bottom-1 sm:bottom-1.5 left-0 right-0">
              {sessions.length > 8 && (
                <div className="text-center mb-0.5">
                  <span className={`text-xs ${isCurrent ? 'text-white' : hasLiveSession ? 'text-green-600' : hasRecentlyUpdated ? 'text-yellow-600' : 'text-cyan-600'}`}>
                    +{sessions.length - 8}
                  </span>
                </div>
              )}
              <div className="flex justify-center gap-0.5 sm:gap-1">
                {sessions.slice(0, 8).map((session, idx) => {
                  const sId = parseInt(session.id, 10);
                  const status = sessionStatuses[sId] || session.session_status || session.status;
                  const isLive = status === 'live' || session.started === true;
                  const isRecent = recentlyUpdatedSessions.has(sId);
                  return (
                    <div 
                      key={idx} 
                      className={`w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full ${
                        isLive ? 'bg-green-500 animate-pulse' : 
                        isRecent ? 'bg-yellow-500' :
                        isCurrent ? 'bg-white' : 'bg-cyan-500'
                      }`}
                    />
                  );
                })}
              </div>
            </div>
          )}
        </div>
      );
    }

    // Add next month days to fill the grid
    const totalCells = 42;
    const remainingCells = totalCells - days.length;
    for (let day = 1; day <= remainingCells; day++) {
      const nextMonthDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, day);
      const sessions = getSessionsForDate(nextMonthDate);
      const hasSessions = sessions.length > 0;

      days.push(
        <div key={`next-${day}`} className="p-1.5 sm:p-3 text-slate-300 text-xs sm:text-sm relative cursor-pointer hover:bg-slate-50 rounded-lg"
             onClick={() => setSelectedDate(nextMonthDate)}>
          {day}
          {hasSessions && (
            <div className="absolute bottom-1 sm:bottom-1.5 left-0 right-0">
              {sessions.length > 8 && (
                <div className="text-center mb-0.5">
                  <span className="text-xs text-cyan-600">+{sessions.length - 8}</span>
                </div>
              )}
              <div className="flex justify-center gap-0.5 sm:gap-1">
                {sessions.slice(0, 8).map((session, idx) => (
                  <div key={idx} className="w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full bg-cyan-500" />
                ))}
              </div>
            </div>
          )}
        </div>
      );
    }

    return days;
  };

  // Session card component
  function SelectedDaySessionCard({ session }) {
    const sessionId = parseInt(session.id, 10);
    const wsStatus = sessionStatuses[sessionId];
    
    const isLive = wsStatus === 'live' || 
                   session.session_status === 'live' || 
                   session.status === 'live' ||
                   session.started === true;
    
    const isEnded = wsStatus === 'ended' || 
                   session.session_status === 'ended' || 
                   session.status === 'ended' ||
                   session.ended === true;;
    
    const hasRecording = session.recording_enabled || 
                        session.recordingEnabled || 
                        session.recording_url || 
                        session.recordingUrl;
    
    const startTime = session.startTime || session.start_time;
    const endTime = session.endTime || session.end_time;
    
    const actualStartTime = session.actualStartTime || session.actual_start_time;
    const actualEndTime = session.actualEndTime || session.actual_end_time;
    
    const totalConnectedTime = session.totalConnectedTime || session.total_connected_time;
    
    const now = new Date();
    const sessionStart = new Date(startTime);
    const sessionEnd = new Date(endTime);
    
    const isUpcoming = !isLive && !isEnded && sessionEnd > now;
    const isPast = isEnded || (!isLive && !isUpcoming);
    
    const teacherCanJoin = (() => {
      if (!isTeacher) return false;
      if (isLive) return true;
      if (isPast || now > sessionEnd) return false;
      
      const fiveMinutesBefore = new Date(sessionStart.getTime() - 5 * 60 * 1000);
      return now >= fiveMinutesBefore && now <= sessionEnd;
    })();
    
    const studentWaitingForTeacher = (() => {
      if (isTeacher || isLive || isPast) return false;
      const fiveMinutesBefore = new Date(sessionStart.getTime() - 5 * 60 * 1000);
      return now >= fiveMinutesBefore && now <= sessionEnd;
    })();
    
    const canJoin = isLive || (isTeacher && teacherCanJoin);
    
    const isRecentlyUpdated = recentlyUpdatedSessions.has(sessionId);

    const handleJoinSessionClick = () => {
      if (!canJoin) return;
      
      if (hasRecording) {
        setRecordingConsentSession(session);
      } else {
        handleJoinSession(session.id);
      }
    };

    const handleCourseClick = () => {
      if (session.course?.id) {
        navigate(`/courses/${session.course.id}/upcoming`);
      }
    };

    return (
      <div className={`bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-all border ${
        isLive ? 'border-green-400' : 
        isRecentlyUpdated ? 'border-yellow-400 shadow-yellow-100' :
        'border-slate-200'
      }`}>
        <div className="flex flex-col gap-4">
          {/* Title row with session number and course name */}
          <div className="flex items-center gap-3 flex-wrap">
            <h3 className="text-base font-semibold text-slate-800 truncate">
              {session.title}
            </h3>
            {(session.session_number || session.sessionNumber) && (
              <span className="text-xs px-2 py-0.5 bg-cyan-50 rounded-full text-cyan-600 flex-shrink-0">
                #{session.session_number || session.sessionNumber}
              </span>
            )}
            {session.course?.name && (
              <button
                onClick={handleCourseClick}
                className="text-xs px-2 py-1 bg-purple-50 text-purple-700 rounded-full font-medium flex-shrink-0 hover:bg-purple-100 transition-colors"
              >
                {session.course.name}
              </button>
            )}
            {isLive && (
              <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full flex-shrink-0 font-medium">
                {t('calendar.status.liveNow')}
              </span>
            )}
            {hasRecording && !isPast && (
              <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded-full flex-shrink-0 font-medium flex items-center">
                <div className="w-1.5 h-1.5 bg-red-600 rounded-full mr-1 animate-pulse" />
                {isLive ? t('calendar.status.recordingOn') : t('calendar.status.recordingEnabled')}
              </span>
            )}
            {hasRecording && isPast && (
              <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full flex-shrink-0 font-medium">
                {t('calendar.status.wasRecorded')}
              </span>
            )}
            {isRecentlyUpdated && !isLive && (
              <span className="text-xs px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded-full flex-shrink-0">
                {t('calendar.status.updated')}
              </span>
            )}
          </div>

          {hasRecording && !isPast && (
            <div className="p-2 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
              <VideoCameraIcon className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
              <div className="text-xs text-red-700">
                <span className="font-medium">
                  {isLive ? t('calendar.recording.isBeingRecorded') : t('calendar.recording.willBeRecorded')}
                </span>
                <p className="mt-1 text-red-600">
                  {t('calendar.recording.contentCaptured')}
                </p>
              </div>
            </div>
          )}

          {/* Session date/time section */}
          <div className="flex flex-col gap-2">
            <div className="flex items-start justify-between gap-3">
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-4">
                  <span className="flex items-center text-sm">
                    <CalendarIcon className="w-3.5 h-3.5 mr-1 flex-shrink-0" />
                    {formatDate(startTime, i18n.language)}
                  </span>
                  <span className="flex items-center text-xs text-slate-600">
                    <ClockIcon className="w-3 h-3 mr-1 flex-shrink-0" />
                    {formatTime(startTime)} - {formatTime(endTime)}
                  </span>
                </div>
              </div>
              
              {(isUpcoming || studentWaitingForTeacher || isLive) && (
                <button
                  onClick={canJoin ? handleJoinSessionClick : undefined}
                  disabled={!canJoin}
                  className={`px-3 py-1.5 rounded-lg transition-colors flex items-center text-sm font-medium ${
                    isLive
                      ? 'bg-green-600 text-white hover:bg-green-700 cursor-pointer'
                      : canJoin
                        ? 'bg-cyan-600 text-white hover:bg-cyan-700 cursor-pointer'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                  title={!canJoin && !isTeacher ? t('calendar.tooltips.waitingForTeacher') : 
                         !canJoin && isTeacher ? t('calendar.tooltips.teacherCanJoin5Min') : ''}
                >
                  <VideoCameraIcon className="w-4 h-4 mr-1.5" />
                  {isLive ? t('calendar.buttons.joinLive') : t('calendar.buttons.join')}
                </button>
              )}
            </div>
            
            {isLive && (actualStartTime || session.secondsSinceStart !== undefined) && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-green-600 flex items-center">
                  <CheckCircleIcon className="w-3 h-3 mr-1" />
                  <LiveSessionTimer 
                    secondsSinceStart={session.secondsSinceStart || session.seconds_since_start}
                    actualStartTime={actualStartTime}
                    t={t}
                  />
                </span>
              </div>
            )}
            
            {isPast && actualStartTime && actualEndTime && (
              <div className="flex items-center gap-4">
                <span className="text-xs text-green-600 flex items-center">
                  <CheckCircleIcon className="w-3 h-3 mr-1" />
                  {t('calendar.time.actual')}: {formatTime(actualStartTime)} - {formatTime(actualEndTime)}
                </span>
                <span className="text-xs text-slate-500">
                  (<DynamicTimeAgo time={actualEndTime} t={t} />)
                </span>
              </div>
            )}
            
            {isUpcoming && !isLive && !studentWaitingForTeacher && now < sessionStart && (
              <span className="text-xs text-blue-600 font-medium">
                ({t('calendar.time.startsIn')} <CountdownTimer startTime={startTime} t={t} />)
              </span>
            )}
            
            {studentWaitingForTeacher && (
              <span className="text-xs text-orange-600 font-medium">
                {t('calendar.status.waitingForTeacher')}
              </span>
            )}
          </div>

          {isPast && !isLive && (
            <div className="flex items-center gap-3 flex-wrap">
              <span className={`text-xs px-2 py-1 rounded-full flex items-center ${
                session.attended
                  ? 'bg-green-100 text-green-700'
                  : 'bg-red-100 text-red-700'
              }`}>
                {session.attended ? (
                  <>
                    <CheckCircleIcon className="w-3 h-3 mr-1" />
                    {t('calendar.status.attended')}
                  </>
                ) : (
                  <>
                    <XCircleIcon className="w-3 h-3 mr-1" />
                    {t('calendar.status.absent')}
                  </>
                )}
              </span>
              
              {session.attended && totalConnectedTime && (
                <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full">
                  {t('calendar.status.connected')}: {formatDuration(totalConnectedTime, t)}
                </span>
              )}
            </div>
          )}
          
          <div className="flex items-center justify-between">
            <span className="flex items-center text-sm text-slate-600">
              <UserIcon className="w-3.5 h-3.5 mr-1 flex-shrink-0" />
              {session.tutor_name || session.tutorName || session.tutor?.name || t('calendar.noTutorAssigned')}
            </span>
            
            {/* Status badges */}
            {!isLive && !isPast && studentWaitingForTeacher && (
              <span className="text-xs px-2 py-1 rounded-full font-medium bg-orange-100 text-orange-700">
                {t('calendar.status.waitingForTeacher')}
              </span>
            )}
            {!isLive && !isPast && teacherCanJoin && !studentWaitingForTeacher && (
              <span className="text-xs px-2 py-1 rounded-full font-medium bg-yellow-100 text-yellow-700">
                {t('calendar.status.readyToStart')}
              </span>
            )}
            {!isLive && !isPast && !teacherCanJoin && !studentWaitingForTeacher && now < sessionStart && (
              <span className="text-xs px-2 py-1 rounded-full font-medium bg-slate-100 text-slate-700">
                {t('calendar.status.upcoming')}
              </span>
            )}
            {isPast && !isLive && (
              <span className="text-xs px-2 py-1 rounded-full font-medium bg-gray-100 text-gray-700">
                {t('calendar.status.completed')}
              </span>
            )}
            {isLive && (
              <span className="text-xs px-2 py-1 rounded-full font-medium bg-green-100 text-green-700">
                {t('calendar.status.inProgress')}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Get translated day names - short for mobile, full for desktop
  const dayNamesShort = [
    t('calendar.days.monShort', 'M'),
    t('calendar.days.tueShort', 'T'),
    t('calendar.days.wedShort', 'W'),
    t('calendar.days.thuShort', 'T'),
    t('calendar.days.friShort', 'F'),
    t('calendar.days.satShort', 'S'),
    t('calendar.days.sunShort', 'S')
  ];

  const dayNamesFull = [
    t('calendar.days.mon'),
    t('calendar.days.tue'),
    t('calendar.days.wed'),
    t('calendar.days.thu'),
    t('calendar.days.fri'),
    t('calendar.days.sat'),
    t('calendar.days.sun')
  ];

  return (
    <div className="w-full h-full overflow-y-auto">
      <div className="mx-auto px-4 sm:px-6 py-4 sm:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 max-w-7xl mx-auto">
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-3 sm:p-5">
              <div className="flex items-center justify-between mb-5">
                <h1 className="text-xl sm:text-2xl font-bold text-slate-800">{t('calendar.title')}</h1>
                <div className="flex items-center gap-2">
                  <button 
                    onClick={() => setCurrentMonth(startOfMonth(subMonths(currentMonth, 1)))}
                    className="p-1.5 sm:p-2 rounded-lg sm:rounded-xl hover:bg-slate-100 text-slate-600 transition"
                  >
                    <ChevronLeftIcon className="w-3 h-3 sm:w-4 sm:h-4" />
                  </button>
                  <span className="text-sm sm:text-base text-slate-800 font-medium min-w-[120px] sm:min-w-[140px] text-center">
                    {format(currentMonth, 'MMMM yyyy', { locale: dateLocale })}
                  </span>
                  <button 
                    onClick={() => setCurrentMonth(startOfMonth(addMonths(currentMonth, 1)))}
                    className="p-1.5 sm:p-2 rounded-lg sm:rounded-xl hover:bg-slate-100 text-slate-600 transition"
                  >
                    <ChevronRightIcon className="w-3 h-3 sm:w-4 sm:h-4" />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-7 gap-1 sm:gap-1.5 mb-2">
                {dayNamesFull.map((day, index) => (
                  <div key={index} className="p-1.5 sm:p-3 text-xs sm:text-sm font-medium text-slate-500 text-center">
                    <span className="sm:hidden">{dayNamesShort[index]}</span>
                    <span className="hidden sm:inline">{day}</span>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1 sm:gap-1.5">
                {renderCalendar()}
              </div>
              {error && !isLoading && (
                <div className="mt-4">
                  <p className="text-center text-red-600 bg-red-50 p-3 rounded-md text-sm">
                    {error}
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-4 sm:p-6">
              <h2 className="text-lg sm:text-xl font-bold text-slate-800 mb-4 sm:mb-5">
                {format(selectedDate, 'EEEE, MMMM d', { locale: dateLocale })}
              </h2>

              {!isLoading && getSessionsForDate(selectedDate).length === 0 && (
                <div>
                  <p className="text-sm text-slate-500">{t('calendar.noSessionsScheduled')}</p>
                  {isTeacher && (
                    <p className="text-sm text-slate-500 mt-2">
                      {t('calendar.planNewSession', 'To plan a new session, go to')}{' '}
                      <a
                        href="/courses"
                        className="text-cyan-600 hover:text-cyan-700 font-medium underline"
                      >
                        {t('nav.myCourses', 'Mijn vakken')}
                      </a>
                      .
                    </p>
                  )}
                </div>
              )}
              {getSessionsForDate(selectedDate).length > 0 && (
                <div className="space-y-5">
                  {getSessionsForDate(selectedDate)
                    .sort((a, b) => {
                      const aTime = a.startTime || a.start_time;
                      const bTime = b.startTime || b.start_time;
                      if (!aTime || !bTime) return 0;
                      try {
                        return parseISO(aTime).getTime() - parseISO(bTime).getTime();
                      } catch {
                        return 0;
                      }
                    })
                    .map(session => (
                      <SelectedDaySessionCard key={session.id} session={session} />
                    ))
                  }
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {recordingConsentSession && (
        <RecordingConsentModal
          session={recordingConsentSession}
          onConsent={() => {
            handleJoinSession(recordingConsentSession.id);
            setRecordingConsentSession(null);
          }}
          onCancel={() => setRecordingConsentSession(null)}
        />
      )}
    </div>
  );
}

export default Calendar;